import React, { useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Space,
  Alert,
  message,
  Tag,
  Divider,
  InputNumber,
  Table,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  EuroOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { controlloFatturaCorriereAPI } from '../services/api.ts';

const { Title, Text, Paragraph } = Typography;

interface FileValidation {
  filename: string;
  valid: boolean;
  errors: string[];
}

interface ValidateResult {
  valid: boolean;
  files: {
    A: FileValidation;
    B: FileValidation;
  };
}

interface UnmatchedB {
  name: string;
  data: string;
  colli: number;
  peso: number;
  totale: number;
}

interface ProcessStats {
  spedizioni_a: number;
  righe_fattura: number;
  abbinate: number;
  non_abbinate_a: number;
  non_abbinate_b: UnmatchedB[];
  oltre_tolleranza: number;
  tolleranza: number;
  totale_fattura: number;
  righe_senza_tot: number[];
}

type Slot = 'A' | 'B';

const SLOT_LABELS: Record<Slot, string> = {
  A: 'A - File Spedizioni (.xls / .xlsx / .ods, primo foglio)',
  B: 'B - Fattura Corriere (.pdf)',
};

const ACCEPT: Record<Slot, string> = {
  A: '.xls,.xlsx,.ods',
  B: '.pdf',
};

// Il detail del backend può essere una stringa (HTTPException) o una lista di
// errori di validazione FastAPI (422): in entrambi i casi un testo leggibile.
const errorText = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
  }
  if (error?.code === 'ECONNABORTED') return 'Tempo di attesa scaduto: il file è troppo grande o il server è occupato.';
  return fallback;
};

const ControlloFatturaCorriere: React.FC = () => {
  const [files, setFiles] = useState<Record<Slot, File | null>>({ A: null, B: null });
  const [fileLists, setFileLists] = useState<Record<Slot, UploadFile[]>>({ A: [], B: [] });
  const [tolerance, setTolerance] = useState<number>(2);
  const [validating, setValidating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validation, setValidation] = useState<ValidateResult | null>(null);
  const [stats, setStats] = useState<ProcessStats | null>(null);

  const allFilesSelected = !!files.A && !!files.B;
  const canProcess = !!validation?.valid;

  const resetResults = () => {
    setValidation(null);
    setStats(null);
  };

  const handleSelect = (slot: Slot, file: File) => {
    setFiles((prev) => ({ ...prev, [slot]: file }));
    setFileLists((prev) => ({
      ...prev,
      [slot]: [{ uid: slot, name: file.name, status: 'done' } as UploadFile],
    }));
    resetResults();
  };

  const handleRemove = (slot: Slot) => {
    setFiles((prev) => ({ ...prev, [slot]: null }));
    setFileLists((prev) => ({ ...prev, [slot]: [] }));
    resetResults();
  };

  const handleValidate = async () => {
    if (!files.A || !files.B) return;
    setValidating(true);
    setValidation(null);
    setStats(null);
    try {
      const data: ValidateResult = await controlloFatturaCorriereAPI.validate(files.A, files.B, tolerance);
      setValidation(data);
      if (data.valid) {
        message.success('Validazione superata: puoi avviare il controllo.');
      } else {
        message.warning('Validazione non superata: correggi i file segnalati.');
      }
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante la validazione dei file'));
    } finally {
      setValidating(false);
    }
  };

  const handleProcess = async () => {
    if (!files.A || !files.B) return;
    setProcessing(true);
    setStats(null);
    try {
      const { stats: s, filenames } = await controlloFatturaCorriereAPI.process(files.A, files.B, tolerance);
      setStats(s as ProcessStats);
      message.success(`Controllo completato. File scaricato: ${filenames.join(', ')}`);
    } catch (error: any) {
      message.error(errorText(error, 'Errore durante il controllo'));
    } finally {
      setProcessing(false);
    }
  };

  const renderUpload = (slot: Slot) => (
    <div>
      <Text strong>{SLOT_LABELS[slot]}</Text>
      <div style={{ marginTop: 4 }}>
        <Upload
          accept={ACCEPT[slot]}
          maxCount={1}
          fileList={fileLists[slot]}
          beforeUpload={(f) => {
            handleSelect(slot, f as unknown as File);
            return false;
          }}
          onRemove={() => handleRemove(slot)}
        >
          <Button icon={slot === 'B' ? <FilePdfOutlined /> : <UploadOutlined />}>
            Seleziona file ({ACCEPT[slot].replace(/,/g, ' / ')})
          </Button>
        </Upload>
      </div>
    </div>
  );

  const renderFileResult = (slot: Slot, info?: FileValidation) => {
    if (!info) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <Space>
          {info.valid ? <Tag color="green">{slot} OK</Tag> : <Tag color="red">{slot} ERRORE</Tag>}
          <Text type="secondary">{info.filename}</Text>
        </Space>
        {info.errors.length > 0 && (
          <ul style={{ margin: '4px 0 0 0', color: '#cf1322' }}>
            {info.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const unmatchedColumns = [
    { title: 'Data', dataIndex: 'data', key: 'data', width: 80 },
    { title: 'Nome (fattura)', dataIndex: 'name', key: 'name' },
    { title: 'Colli', dataIndex: 'colli', key: 'colli', width: 70 },
    { title: 'Peso', dataIndex: 'peso', key: 'peso', width: 80 },
    {
      title: 'Totale €',
      dataIndex: 'totale',
      key: 'totale',
      width: 100,
      render: (v: number) => v?.toFixed(2),
    },
  ];

  return (
    <div>
      <Title level={3}>
        <FileExcelOutlined /> Controllo Fattura Corriere
      </Title>
      <Paragraph type="secondary">
        Confronta le spedizioni del <strong>file A</strong> (primo foglio, colonne{' '}
        <Text code>DATA</Text>/<Text code>MITTENTE</Text>/<Text code>DESTINATARIO</Text>/.../
        <Text code>TOT</Text>) con la <strong>fattura PDF del corriere</strong> (formato BRT).
        Per ogni spedizione il totale fatturato è <Text code>TRASPORTO + VARIE</Text>; l'abbinamento
        usa data, peso, colli, volume e nome (in fattura i nomi sono troncati). Nella copia di A
        scaricata vengono aggiunte le colonne <Text code>Fattura</Text> e <Text code>Differenza</Text>{' '}
        (Fattura − TOT) dopo l'ultima colonna usata:
        <Tag color="orange" style={{ marginLeft: 6 }}>arancione</Tag> = fatturato oltre la tolleranza,
        <Tag color="red">rosso</Tag> = spedizione non trovata in fattura. L'originale non viene toccato.
      </Paragraph>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {renderUpload('A')}
          {renderUpload('B')}

          <div>
            <Text strong>
              <EuroOutlined /> Tolleranza differenza (€)
            </Text>
            <div style={{ marginTop: 4 }}>
              <InputNumber
                min={0}
                step={0.5}
                value={tolerance}
                onChange={(v) => {
                  setTolerance(v ?? 0);
                  resetResults();
                }}
                style={{ width: 120 }}
                className="no-uppercase-modal"
              />
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Evidenzia in arancione le spedizioni fatturate oltre TOT + tolleranza.
              </Text>
            </div>
          </div>

          <Space>
            <Button
              type="default"
              icon={<CheckCircleOutlined />}
              onClick={handleValidate}
              loading={validating}
              disabled={!allFilesSelected || processing}
            >
              Valida file
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleProcess}
              loading={processing}
              disabled={!canProcess || validating}
            >
              Avvia controllo
            </Button>
          </Space>
        </Space>
      </Card>

      {validation && (
        <Card style={{ marginTop: 16 }}>
          <Alert
            type={validation.valid ? 'success' : 'error'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              validation.valid
                ? 'Tutti i file sono validi. Puoi avviare il controllo.'
                : 'Sono presenti errori di validazione: correggi i file indicati.'
            }
          />
          <Divider orientation="left">Dettaglio validazione</Divider>
          {renderFileResult('A', validation.files.A)}
          {renderFileResult('B', validation.files.B)}
        </Card>
      )}

      {stats && (
        <Card style={{ marginTop: 16 }} title="Risultato controllo">
          <Row gutter={16}>
            <Col span={4}>
              <Statistic title="Spedizioni (A)" value={stats.spedizioni_a} />
            </Col>
            <Col span={4}>
              <Statistic title="Righe fattura (B)" value={stats.righe_fattura} />
            </Col>
            <Col span={4}>
              <Statistic
                title="Abbinate"
                value={stats.abbinate}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Oltre tolleranza"
                value={stats.oltre_tolleranza}
                valueStyle={{ color: stats.oltre_tolleranza > 0 ? '#fa8c16' : undefined }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="A senza match"
                value={stats.non_abbinate_a}
                valueStyle={{ color: stats.non_abbinate_a > 0 ? '#cf1322' : undefined }}
              />
            </Col>
            <Col span={4}>
              <Statistic title="Totale fattura €" value={stats.totale_fattura} precision={2} />
            </Col>
          </Row>
          {stats.righe_senza_tot && stats.righe_senza_tot.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message={`${stats.righe_senza_tot.length} righe del file A hanno DATA o MITTENTE ma non TOT e non sono state considerate spedizioni (righe ${stats.righe_senza_tot.join(', ')}).`}
            />
          )}
          {stats.non_abbinate_b.length > 0 && (
            <>
              <Divider orientation="left">
                Righe della fattura NON abbinate a nessuna spedizione ({stats.non_abbinate_b.length})
              </Divider>
              <Table<UnmatchedB>
                rowKey={(r) => `${r.data}-${r.name}-${r.totale}`}
                columns={unmatchedColumns}
                dataSource={stats.non_abbinate_b}
                size="small"
                pagination={false}
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default ControlloFatturaCorriere;
